import { useEffect, useRef, useState } from 'react';
import { labelGroupCaption } from '../labelVocab';
import { BlockedBanner } from './BlockedBanner';
import { WaitingBanner, WaitingOnField } from './WaitingOn';
import { Links } from './Links';
import { MarkdownEditor } from './MarkdownEditor';
import { QueryState } from './QueryState';
import { ParentLink } from './ParentLink';
import { Subtasks, SubtaskOffer } from './Subtasks';
import { AssigneeLine } from './AssigneeChip';
import { FailureNote } from './FailureNote';
import { useProjectRole } from '../roles';
import { STATUS_VERB, deleteTaskPrompt } from '../taskVocab';
import { useAccountDay } from '../accountDay';
import { readableInstant } from '../dateLabels';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getTask,
  updateTask,
  completeTask,
  cloneTask,
  deleteTask,
  listChecklist,
  listTaskReminders,
  addChecklistItem,
  updateChecklistItem,
  deleteChecklistItem,
  promoteChecklistItem,
  reorderChecklist,
  listComments,
  addComment,
  getGoalTree,
  listTaskAttachments,
  uploadAttachment,
  deleteAttachment,
  downloadAttachment,
  ApiError,
  type ChecklistItem,
  listLabels,
  listLabelGroups,
  listProjects,
  setTaskLabels,
  listLinks,
  type GoalTreeNode,
  type Task,
} from '../api';
import { MultiSelect } from './MultiSelect';
import { ColorPicker } from './ColorPicker';
import { FacetSection } from './FacetSection';
import {
  goalOptions,
  renderGoalOption,
  statusOptions,
  renderStatusOption,
  priorityOptions,
  renderPriorityOption,
} from './pickerOptions';
import { Select } from './Select';
import { taskOpener, useFocusOnOpen } from '../useFocusOnOpen';

/*
  `blocked` is deliberately absent.

  Nothing in the codebase ever writes it — blocked is *derived* from open blocking links, which is
  what the pill on the row and `?blocked=true` both read. Offering it here let you write a status
  that matches no derived state, so the task vanished from every status view *and* from the
  blocked filter at the same time. A ghost.
*/
const POINTS = [1, 2, 3, 5, 8, 13];

/**
 * The sections of the panel that cost nothing until the task holds one.
 *
 * Named as a union rather than left as strings because the same key has to line up in three
 * places — the emptiness table, `shows`, and the `asked` set — and a typo in any one of them
 * would silently mean "always empty" or "never asked for", neither of which fails loudly.
 */
type Facet =
  'labels' | 'color' | 'goals' | 'checklist' | 'attachments' | 'subtasks' | 'links' | 'notes';

/**
 * Checklist reordering uses the native HTML drag events rather than @dnd-kit.
 * The timeline in Phase 4 has drag semantics no library handles well and will pull
 * @dnd-kit in; a nine-item list does not need it.
 */
export function Checklist({
  taskId,
  projectId,
  startOpen = false,
}: {
  taskId: string;
  /** The task's project, or null for personal work. Required — see `Subtasks`. */
  projectId: string | null;
  /**
   * The reader already asked for this section, from the offer in *More*.
   *
   * Since Phase 102 the **panel** keeps the offer in its disclosure and decides whether this
   * renders at all, so seeding the flag is what makes "press `+ a step`" land on the form rather
   * than on a second offer.
   *
   * The empty branch below is not a fallback and must not be deleted: `TaskView` — the full view —
   * renders this and the other four sections *ungated* and has no disclosure of its own, so there
   * the branch is the offer. That surface has the room the panel did not, which is why the change
   * stopped at the panel; the control count four rounds measured was measured at 1280 in a 384px
   * drawer.
   */
  startOpen?: boolean;
}) {
  const queryClient = useQueryClient();
  const [label, setLabel] = useState('');
  /** Opened from the offer in *More*, or from the empty branch below if it is ever reached. */
  const [adding, setAdding] = useState(startOpen);
  const [dragging, setDragging] = useState<string | null>(null);

  const items = useQuery({
    queryKey: ['checklist', taskId],
    queryFn: () => listChecklist(taskId),
  });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['checklist', taskId] });

  const add = useMutation({
    mutationFn: (l: string) => addChecklistItem(taskId, l),
    onSuccess: () => {
      setLabel('');
      void invalidate();
    },
  });
  const toggle = useMutation({
    mutationFn: (item: ChecklistItem) =>
      updateChecklistItem(taskId, item.id, { done: item.doneAt === null }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteChecklistItem(taskId, id),
    onSuccess: invalidate,
  });
  const promote = useMutation({
    mutationFn: (id: string) => promoteChecklistItem(taskId, id),
    /*
      Everything, not just the checklist.

      The step leaves this list and arrives as a subtask in the section below, which moves the
      parent's `K/N` on six other surfaces. `invalidate` here is the checklist's own key, which
      would leave every one of those reading the old number.
    */
    onSuccess: () => queryClient.invalidateQueries(),
  });
  const reorder = useMutation({
    mutationFn: (ids: string[]) => reorderChecklist(taskId, ids),
    onSuccess: invalidate,
  });

  /*
    Two levels, because the server has two.

    Writing a step, deleting one, promoting one and reordering are all `write`: they change what
    the task says it is made of. Ticking one is `assign` — `updateChecklistItem` splits on exactly
    that, `structural ? 'write' : 'assign'` — because a tick is a fact about progress, which is the
    line the ladder draws everywhere else. So a developer gets a live checkbox and a dead pencil,
    which is precisely what their role means and was previously four silent 403s.
  */
  const role = useProjectRole(projectId);
  const mayEdit = role.can('write');
  const mayTick = role.can('assign');

  const drop = (targetId: string) => {
    if (!mayEdit) return;
    if (!dragging || dragging === targetId || !items.data) return;
    const ids = items.data.map((i) => i.id);
    const from = ids.indexOf(dragging);
    const to = ids.indexOf(targetId);
    ids.splice(to, 0, ids.splice(from, 1)[0]!);
    setDragging(null);
    reorder.mutate(ids);
  };

  const done = items.data?.filter((i) => i.doneAt !== null).length ?? 0;

  /*
    Empty costs one line, not a panel.

    On an empty task the reviewer counted 475px of four empty panels out of a 589px view. The
    facet sections in this same panel already collapse when there is nothing in them; these two did
    not, and being the same size as each other while doing opposite things is what made the pair
    read as "a decision I now have to make".

    The line says what a step *is*, because the distinction from a subtask is the thing nobody
    could see: a step is a line you tick, a subtask is a piece of work you can put on a day.
  */
  if (!items.data?.length && !adding) {
    return (
      <p className="panel-empty">
        {/* Greyed rather than dropped, like the offers around it — see `Subtasks`. */}
        <button className="link" onClick={() => setAdding(true)} disabled={!mayEdit}>
          + a step
        </button>{' '}
        {/* Kept where the other offers lost theirs: this sentence *is* an earlier fix. The
            difference between a step and a subtask was the thing nobody could see, and the two
            offers sit next to each other. */}
        <span className="subtext">a line to tick off, with no date of its own</span>
      </p>
    );
  }

  return (
    <div className="panel">
      <h3>
        Checklist{' '}
        {items.data?.length ? (
          <span className="muted">
            {done}/{items.data.length}
          </span>
        ) : null}
      </h3>

      <ul className="checklist">
        {items.data?.map((item) => (
          <li
            key={item.id}
            draggable={mayEdit}
            onDragStart={() => setDragging(item.id)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => drop(item.id)}
            className={dragging === item.id ? 'dragging' : ''}
          >
            <span className="grip" aria-hidden="true">
              ⠿
            </span>
            <input
              type="checkbox"
              checked={item.doneAt !== null}
              disabled={!mayTick}
              onChange={() => toggle.mutate(item)}
            />
            <span className={item.doneAt ? 'struck' : ''}>{item.label}</span>
            {/*
              The step turned out to be a Tuesday's work.

              Without this the user retypes the label as a new task, sets its parent and deletes
              the step — three actions and a chance to lose the wording, which is the part they
              actually thought about.

              Offered only on a step that is still open: promoting a finished one would create
              work that is already done.
            */}
            {item.doneAt === null && (
              <button
                className="link subtle"
                title="Make it a subtask"
                aria-label={`Make “${item.label}” a subtask`}
                disabled={!mayEdit}
                onClick={() => promote.mutate(item.id)}
              >
                ↑
              </button>
            )}
            <button
              className="link danger"
              onClick={() => remove.mutate(item.id)}
              disabled={!mayEdit}
            >
              ×
            </button>
          </li>
        ))}
      </ul>

      <form
        className="inline-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (label.trim()) add.mutate(label.trim());
        }}
      >
        <input
          value={label}
          placeholder="Add a step"
          disabled={!mayEdit}
          onChange={(e) => setLabel(e.target.value)}
        />
        <button type="submit" disabled={!label.trim() || !mayEdit}>
          Add
        </button>
      </form>
    </div>
  );
}

export function Thread({
  taskId,
  startOpen = false,
}: {
  taskId: string;
  /** Asked for from the offer in *More* — see `Checklist`. */
  startOpen?: boolean;
}) {
  const queryClient = useQueryClient();
  /* Each line's time on the account's clock — a comment written at 20:35 in Warsaw is not one
     written the next morning because the laptop reading it sits in Kiritimati. */
  const { timezone } = useAccountDay();
  const [body, setBody] = useState('');
  /* Opened by asking — see the empty return below. */
  const [noting, setNoting] = useState(startOpen);
  const comments = useQuery({
    queryKey: ['comments', taskId],
    queryFn: () => listComments(taskId),
  });

  const add = useMutation({
    mutationFn: (b: string) => addComment(taskId, b),
    onSuccess: () => {
      setBody('');
      void queryClient.invalidateQueries({ queryKey: ['comments', taskId] });
    },
  });

  /*
    A task nobody has said anything about yet.

    A heading, "Nothing yet." and a note field with a Post button is four lines to report that
    nothing has happened — on the majority of tasks, which are made and finished without anyone
    writing a word. The thread appears the moment there is a thread, and the way to start one is
    still one press away. Same argument as the panels above it.
  */
  if (comments.data?.length === 0 && !noting) {
    return (
      <p className="panel-empty">
        <button className="link" onClick={() => setNoting(true)} data-testid="add-note">
          + a note
        </button>
      </p>
    );
  }

  return (
    <div className="panel">
      <h3>Activity</h3>
      <ul className="thread">
        {comments.data?.map((c) => {
          /*
            Who spoke, before what they said.

            The line used to carry `user` / `agent` / `system` and nothing else, so on a shared
            project the thread could not say who wrote anything, and the signatures people typed
            into the text filled the gap badly: a note of Priya's opening "Sam: …" — she was
            addressing him — read as Sam's, directly above a genuinely-Sam entry tagged `agent`.

            `author` comes from the service, resolved through the project's membership. Null is a
            system entry or a personal thread; a null name inside it is somebody who has left, and
            they get **a former member** rather than a blank, because "nobody" and "someone who
            is gone" are different facts and the entry is still theirs.
          */
          const who = c.author ? (c.author.displayName ?? 'a former member') : null;
          /*
            The tag survives as a badge, not as the identity — `agent` means somebody's agent
            spoke for them, which is not the same event as them speaking, and collapsing it into
            the name would lose the distinction the thread exists to record. `user` beside a name
            is the one that says nothing, so it is dropped there and kept where there is no name
            (a personal thread), which is what that line has always shown.
          */
          const tag = c.kind !== 'user' || who === null ? c.kind : null;
          /*
            `entry-named` moves the line from `[tag] body` to a header above the body. A name is
            too wide to sit in a shared first column — "Priya (product architect)" would set that
            column's width for every system entry in the thread — and a system entry has no name,
            so it keeps the compact two-column shape it has always had.
          */
          return (
            <li key={c.id} className={`entry kind-${c.kind}${who === null ? '' : ' entry-named'}`}>
              <span className="entry-who">
                {who !== null && (
                  <span
                    className={`entry-author${c.author?.displayName === null ? ' muted' : ''}`}
                    data-testid="entry-author"
                  >
                    {who}
                  </span>
                )}
                {tag !== null && <span className="pill">{tag}</span>}
              </span>
              <span className="entry-body">{c.bodyMd}</span>
              <time className="muted">{readableInstant(c.createdAt, timezone)}</time>
            </li>
          );
        })}
      </ul>
      <form
        className="inline-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (body.trim()) add.mutate(body.trim());
        }}
      >
        <input value={body} placeholder="Add a note" onChange={(e) => setBody(e.target.value)} />
        <button type="submit" disabled={!body.trim()}>
          Post
        </button>
      </form>
      {/*
        The box stays enabled for every role: commenting dropped from `assign` to `read` in
        v1.11.0, so a reporter's note is a control that works. What was missing is what happens
        when one does not — the typed text sat in the draft box and the screen said nothing.
      */}
      <FailureNote error={add.error} testId="comment-error" />
    </div>
  );
}

const formatSize = (bytes: number) =>
  bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

/**
 * The reminders set on a task, and whether each has gone out.
 *
 * The one place a *pending* reminder is visible. `visible()` keeps a future `deliverAt` with no
 * `deliveredAt` out of the bell and out of the badge, deliberately — it is not news yet (§5.6) —
 * which until `01a0ffad-d2fb` meant somebody who set three reminders on a task could not see them
 * anywhere until each one fired.
 *
 * Read-only, because nothing in the app creates one by hand: `instantiateTemplate` writes them
 * from a template's `reminderOffsets`. So no "add" form and no `FacetSection` — this is a
 * statement about the task, not a facet to edit.
 *
 * Absent rather than empty when the task has none, which is `WaitingRail`'s rule and the
 * Minimalist's ruling from Phase 40: an empty section is a permanent reminder that a feature
 * exists.
 */
function Reminders({ taskId }: { taskId: string }) {
  const reminders = useQuery({
    queryKey: ['reminders', taskId],
    queryFn: () => listTaskReminders(taskId),
  });
  const { timezone } = useAccountDay();

  const rows = reminders.data ?? [];
  if (rows.length === 0) return null;

  /* The account's own clock. This comment used to say "which is the browser's here", and that was
     only true while the laptop sat in the account's zone (INV-UTC: stored in UTC, judged in the
     account's zone) — a reminder due 09:00 in Warsaw read 20:00 to a laptop in Kiritimati. */
  const when = (iso: string) => readableInstant(iso, timezone, 'weekdayAndTime');

  return (
    <div className="panel" data-testid="task-reminders">
      <h4>Reminders</h4>
      <ul className="plain">
        {rows.map((r) => (
          <li key={r.id} className={r.deliveredAt ? 'reminder sent' : 'reminder'}>
            {/* The tense is the whole point: "sends" is a promise, "sent" is a record. */}
            <span className="muted small">
              {r.deliveredAt ? `sent ${when(r.deliveredAt)}` : `sends ${when(r.deliverAt)}`}
            </span>{' '}
            {r.title}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Attachments({
  taskId,
  projectId,
  startOpen = false,
}: {
  taskId: string;
  /** The task's project, or null for personal work. Required — see `Subtasks`. */
  projectId: string | null;
  /** Asked for from the offer in *More* — see `Checklist`. */
  startOpen?: boolean;
}) {
  /* Revealed by asking — see the note before the empty return below. */
  const [attaching, setAttaching] = useState(startOpen);
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const files = useQuery({
    queryKey: ['attachments', taskId],
    queryFn: () => listTaskAttachments(taskId),
  });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['attachments', taskId] });

  const upload = useMutation({
    mutationFn: (file: File) => uploadAttachment(taskId, file),
    onSuccess: () => {
      setError(null);
      void invalidate();
    },
    onError: (e) =>
      setError(e instanceof ApiError ? e.message : 'Upload failed. Try a smaller file.'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteAttachment(id),
    onSuccess: invalidate,
  });

  /*
    Attaching is `assign` and removing is `write`, which is what the service charges: a file is
    something a developer adds while working, and taking one off the task is a change to what the
    task carries. So a developer may attach and may not delete, and both halves are now drawn that
    way rather than answered after the press — which for attaching was answered *wrongly*, with a
    404 naming the task the reader had open. That message is fixed in the same commit.
  */
  const role = useProjectRole(projectId);
  const mayAttach = role.can('assign');
  const mayRemove = role.can('write');

  /*
    Nothing attached, and nobody asking to attach anything.

    The section drew a heading, "Nothing attached." and a raw `Choose File` control — three lines
    of furniture on the majority of tasks, which never have a file. See the Description panel for
    the same argument.
  */
  if (files.data?.length === 0 && !attaching && !upload.isPending) {
    return (
      <p className="panel-empty">
        <button
          className="link"
          onClick={() => setAttaching(true)}
          data-testid="add-attachment"
          disabled={!mayAttach}
        >
          + a file
        </button>
      </p>
    );
  }

  return (
    <div className="panel">
      <h3>Attachments</h3>
      {error && <p className="banner">{error}</p>}
      <ul>
        {files.data?.length === 0 && <li className="muted">Nothing attached.</li>}
        {files.data?.map((f) => (
          <li key={f.id} className="attachment-row">
            <button
              className="link"
              onClick={async () => {
                // Short-lived signed URL, fetched on demand rather than embedded in
                // the page — a link in the DOM outlives the reason it was granted.
                const { url } = await downloadAttachment(f.id);
                window.open(url, '_blank', 'noopener');
              }}
            >
              {f.filename}
            </button>
            <span className="size">{formatSize(f.sizeBytes)}</span>
            <button
              className="link danger"
              onClick={() => remove.mutate(f.id)}
              disabled={!mayRemove}
            >
              ×
            </button>
          </li>
        ))}
      </ul>
      {/*
        Named, and given a real target.

        It had no accessible name at all — a screen reader announced "file upload button" with no
        indication of what it attaches to — and the browser's own control renders 21px tall, under
        the 24px floor of SC 2.5.8. `.file-field` styles `::file-selector-button` rather than
        replacing the native picker with a label-and-hidden-input trick that would have to
        re-implement keyboard focus.
      */}
      <input
        type="file"
        className="file-field"
        aria-label="Attach a file to this task"
        disabled={upload.isPending || !mayAttach}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) upload.mutate(file);
          e.target.value = '';
        }}
      />
      {upload.isPending && <p className="muted">Uploading…</p>}
    </div>
  );
}

function flattenTree(nodes: GoalTreeNode[]): GoalTreeNode[] {
  return nodes.flatMap((n) => [n, ...flattenTree(n.children)]);
}

/**
 * The first line of a description, capped.
 *
 * A goal's description is markdown and can be pages long. What is wanted here is the sentence
 * somebody wrote to remind themselves why they bothered, which is almost always the first one.
 */
function firstLine(markdown: string): string {
  const line =
    markdown
      .split('\n')
      .find((l) => l.trim() !== '')
      ?.trim() ?? '';
  const clean = line.replace(/^#+\s*/, '');
  return clean.length > 160 ? `${clean.slice(0, 159)}…` : clean;
}

export function TaskDetail({
  taskId,
  onClose,
  onOpenTask,
  onExpand,
}: {
  taskId: string;
  onClose: () => void;
  /** Switches the panel to another task — used to land on a clone right after making it. */
  onOpenTask?: (id: string) => void;
  /** Hands this task to the full read view. */
  onExpand?: () => void;
}) {
  const queryClient = useQueryClient();
  const task = useQuery({ queryKey: ['task', taskId], queryFn: () => getTask(taskId) });
  const goals = useQuery({ queryKey: ['goals', 'tree'], queryFn: getGoalTree });

  /*
    Focus comes in when the panel opens and goes back to the task's row when it closes.

    The panel itself takes it, not the title field. The field is a text input, so focus there makes
    every single-key shortcut a letter in the title and turns Escape into two presses — the first
    only releases the field (`useEscapeReleasesFields`), the second closes — and on a phone it
    raises the keyboard over a panel somebody opened to read. Focusing the `<aside>` announces its
    name, keeps the list's keys live, and puts the title field one Tab away.

    The return is the row's (or card's) title button, looked up by id at close rather than
    remembered: the task the panel shows can change while it is open, and the row for it may be a
    different element by then. See `useFocusOnOpen`.
  */
  const surface = useRef<HTMLElement>(null);
  useFocusOnOpen(true, surface, { returnTo: () => taskOpener(taskId) });

  /*
    Escape closes the panel.

    The shortcuts overlay has always advertised "Esc — Close whatever is open" and this panel was
    the one thing it did not close, so the key that everybody tries first did nothing here.

    Capture rather than bubble, and `stopPropagation` once handled, because the Tasks list also
    listens for Escape on `document` — to clear a multi-selection — and it registers first. In
    bubble order one press would wipe the selection *and* leave the panel open, which is the pair
    of complaints this fixes. A capture listener runs before every bubble listener on the same
    target, so one press now closes exactly one thing.

    Anything opened on top of the panel owns Escape ahead of it — a menu, a goal or label picker's
    listbox, a dialog, the search panel — because those close themselves and the panel underneath
    must stay put. The test for the label picker caught this: Escape closed the whole detail out
    from under an open combobox, so the control the user was aiming at vanished with it.

    Open popups are matched, not `[aria-expanded="true"]`: a disclosure inside the panel that
    happens to be expanded would then block Escape from ever closing the panel at all. And they are
    matched by whether they are *rendered*, not by whether they exist — `Select` keeps its listbox
    in the DOM behind `hidden={!open}`, so a query for the role alone matches a closed picker and
    Escape stops working entirely.
  */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      const overlayOpen = Array.from(
        document.querySelectorAll(
          '[role="menu"], [role="listbox"], [role="dialog"], .search-panel',
        ),
      ).some((el) => el.getClientRects().length > 0);
      if (overlayOpen) return;
      event.preventDefault();
      event.stopPropagation();
      onClose();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  /*
    The panel edits the set it is *about to have*, not the one the server last confirmed.

    Every control here reads `t`, which is this query's data, and `MultiSelect` composes each change
    from the `value` it was rendered with. With the refresh happening only in `onSuccess`, two picks
    made inside one round trip were both composed from the same answer, and the second sent a set
    that had never heard of the first: pick two goals quickly and only the second survived, because
    `updateTask` replaces `goalIds` wholesale (`delete(taskGoals)` then insert). No error, no sign
    on screen — the chip simply was not there.

    So the pending patch goes into the cache before the request leaves. The next composition then
    starts from it, which is the only thing that makes two fast changes add up.

    `cancelQueries` first, because an in-flight read of the old state is free to land after this
    write and undo it — the refetch is the other way this cache gets written.
  */
  const save = useMutation({
    /*
      Serialised on the task, so a second change's request waits for the first to settle.

      `PATCH {goalIds}` is a true replace — `updateTask` does `delete(taskGoals)` then insert — so
      two of them landing out of order leaves whichever answered last, however the sets were
      composed. The optimistic write below fixes what the client *sends*; this fixes what order the
      server *sees*. Both are needed, and neither covers the other.

      `onMutate` still runs the instant `mutate()` is called — in `@tanstack/query-core` the scope
      gates only the retryer around `mutationFn` — so the cache is updated immediately and the next
      composition starts from it even while the previous request is still out.
    */
    scope: { id: `goals-${taskId}` },
    mutationFn: (patch: Parameters<typeof updateTask>[1]) => updateTask(taskId, patch),
    onMutate: async (patch) => {
      /* A read already in flight would land after this write and undo it: the refetch is the other
         thing that writes this cache. */
      await queryClient.cancelQueries({ queryKey: ['task', taskId] });
      const previous = queryClient.getQueryData<Task>(['task', taskId]);
      queryClient.setQueryData<Task>(['task', taskId], (current) =>
        current ? { ...current, ...patch } : current,
      );
      return { previous };
    },
    onError: (_error, _patch, context) => {
      if (context?.previous) queryClient.setQueryData(['task', taskId], context.previous);
    },
    onSuccess: () => queryClient.invalidateQueries(),
  });
  const finish = useMutation({
    mutationFn: () => completeTask(taskId),
    onSuccess: () => queryClient.invalidateQueries(),
  });
  const remove = useMutation({
    mutationFn: () => deleteTask(taskId),
    onSuccess: () => {
      onClose();
      void queryClient.invalidateQueries();
    },
  });
  /*
    Cloning lands you on the copy.

    The copy is in the inbox with the original's dates on it, which is exactly the state you want
    to look at and adjust — and if the panel stayed on the original you would have to go and find
    the new one to do it.
  */
  const clone = useMutation({
    mutationFn: () => cloneTask(taskId),
    onSuccess: (created) => {
      void queryClient.invalidateQueries();
      onOpenTask?.(created.id);
    },
  });

  // The editor is controlled, so the draft lives here. Re-seeded when a different task
  // is opened; not on every refetch, which would discard what is being typed.
  const [description, setDescription] = useState('');
  /* Opened by asking — see the note beside the Description panel. */
  const [writing, setWriting] = useState(false);
  const loadedFor = useRef<string | null>(null);
  useEffect(() => {
    if (task.data && loadedFor.current !== taskId) {
      loadedFor.current = taskId;
      setDescription(task.data.descriptionMd);
    }
  }, [task.data, taskId]);

  /*
    Above the early returns.

    These were below `if (task.isPending) return …`, which is a rules-of-hooks violation — React
    threw on every render of the panel and the whole detail stopped opening. The mutation closes
    over `taskId`, which is a prop, so it does not need the loaded task.
  */
  const allLabels = useQuery({ queryKey: ['labels'], queryFn: listLabels });
  const labelGroups = useQuery({ queryKey: ['label-groups'], queryFn: listLabelGroups });
  // Only to name the project a shared group belongs to — see `labelGroupCaption`.
  const projects = useQuery({ queryKey: ['projects'], queryFn: listProjects });

  /*
    What this task holds, for the sections that fetch their own answer.

    These are the children's *own* query keys — `Attachments`, `Thread` and `Links` each declare
    the same three — so this costs nothing: React Query serves both subscribers from one cache
    entry, and all three requests already went out when the panel opened. Measured on a bare task
    at `f67aa12`: seven `/api/` requests on open, `/attachments`, `/comments` and `/links` among
    them, and the count is unchanged by reading them here.

    That is why there are no counts in the task payload for this. Adding them would have meant
    three more aggregates in `decorate`, which `listTasks` shares — so a 500-row page would pay
    for a question only this panel asks, to learn something the client already had.

    The other four need nothing: `descriptionMd` and `color` are on the task, and
    `checklist.total` / `subtasks.total` have been since `27a5f26`.
  */
  const attachments = useQuery({
    queryKey: ['attachments', taskId],
    queryFn: () => listTaskAttachments(taskId),
  });
  const notes = useQuery({ queryKey: ['comments', taskId], queryFn: () => listComments(taskId) });
  const taskLinks = useQuery({ queryKey: ['links', taskId], queryFn: () => listLinks(taskId) });

  /*
    Which sections the reader has asked for, from the offers inside *More*.

    Held here rather than in each child because the parent is what decides whether a child renders
    at all now, and the two cannot be in different places: a child that owned the flag would have
    to be mounted to be asked, which is exactly what is being avoided.

    A section already holding something is never in this set and never needs to be — `shows` reads
    it as an *or*. So pressing an offer and then adding a step leaves the section on screen for the
    ordinary reason, and the set stops mattering the moment the task answers for itself.
  */
  const [asked, setAsked] = useState<ReadonlySet<Facet>>(() => new Set());
  const ask = (facet: Facet) => setAsked((current) => new Set(current).add(facet));
  const setLabels = useMutation({
    /*
      Serialised, and here it is load-bearing rather than tidy.

      `PUT /api/tasks/:id/labels` **adds**: `applyLabelsUnlogged` ends
      `insert(taskLabels)…onConflictDoNothing()`, and removal is the `DELETE`s this call makes
      first. So an add-only write cannot say "the set is now exactly this", and two overlapping
      sequences can put back what the other just removed — deselect two labels quickly and one of
      them returns on its own. Ordering is the only thing that closes that from the client.

      The alternative was to send one `PATCH {labelIds}`, which replaces server-side and would need
      no ordering at all. It is not taken, and not for tidiness: `restoringPatch` in
      `services/revert.ts` refuses to revert a `task.update` whose patch names `labelIds`, because
      the trail does not record the previous set — while `task.label` and `task.unlabel` each have
      an inverse and revert cleanly, eviction included. Moving the picker to `PATCH` would make
      every label edit made in a browser un-undoable, which is the capability the recording pair
      exists for.
    */
    scope: { id: `labels-${taskId}` },
    /*
      `previous` comes in with the variables, captured at click time from the set the picker was
      rendered with — deliberately *not* read from `task.data` in here.

      That read used to be this line, and under serialisation it becomes wrong in a new way: it
      would run after the earlier click's `onSuccess` had already refreshed the cache, so the set it
      sees already includes the earlier removal and no `DELETE` is emitted for it. The label comes
      back by a different route than the one being fixed. The rendered value is the only set that
      says what *this* click was deciding against.
    */
    mutationFn: ({ next, previous }: { next: string[]; previous: string[] }) =>
      setTaskLabels(taskId, next, previous),
    onMutate: async ({ next }) => {
      await queryClient.cancelQueries({ queryKey: ['task', taskId] });
      const snapshot = queryClient.getQueryData<Task>(['task', taskId]);
      queryClient.setQueryData<Task>(['task', taskId], (current) =>
        current ? { ...current, labelIds: next } : current,
      );
      return { snapshot };
    },
    onError: (_error, _variables, context) => {
      if (context?.snapshot) queryClient.setQueryData(['task', taskId], context.snapshot);
    },
    onSuccess: () => queryClient.invalidateQueries(),
  });

  /*
    What this viewer's role lets them do here.

    Above the early returns, like the mutations, for the same rules-of-hooks reason.

    Only Complete is disabled. The fields below it save on change and sit at three different
    levels — status and labels at `assign`, title and priority at `write` — so greying them
    individually would put five different explanations in a 384px column. Saying once what this
    role may do, and printing the server's refusal when a field is nonetheless refused, tells the
    same truth in one line each. The full view's Edit is what keeps a reporter out of here in the
    first place; the panel is still reachable straight from a row.
  */
  const { can, why } = useProjectRole(task.data?.projectId ?? null);
  /*
    The sentence covers the surface, the gates cover their own controls.

    It read `why('assign')`, which is the level Complete answers to — so a *developer* got no
    sentence at all, while the four sections below offered a step, a subtask, a link and a file
    that their role could not produce. `why('write')` is the strongest level anything on this
    panel needs, so it is non-null exactly when something here is refused; the role sentence is
    the same one whatever level asked for it, because `roleReason` keys on the role.

    `whyNotProgress` stays separate and stays at `assign`: it is the tooltip on Complete, and a
    developer holding the task may press it. One sentence explaining the screen, one explaining
    the button, and they are not the same question.
  */
  const whyNot = why('write');
  const whyNotProgress = why('assign');

  /*
    The same element in every state, so the ref and the focus survive the load.

    A side panel and not a dialog: the list beside it stays live — its rows, its keys, a click on
    another row — and nothing traps Tab, so `role="complementary"` is the honest role. A `dialog`
    would also tell the panel's own Escape guard above that something is open over it, and Escape
    would stop closing it at all. Named by the task's title, so a screen reader says which task
    opened rather than "complementary".
  */
  const asideProps = {
    className: 'detail',
    ref: surface,
    tabIndex: -1,
    role: 'complementary',
    'aria-label': task.data?.title ?? 'Task',
  } as const;

  if (task.isPending || task.isError)
    return (
      <aside {...asideProps}>
        <QueryState query={task} className="muted" />
      </aside>
    );
  if (task.error || !task.data)
    return (
      <aside {...asideProps}>
        <p className="muted">That task is gone.</p>
        <button onClick={onClose}>Close</button>
      </aside>
    );

  const t = task.data;
  const goalPickerOptions = goalOptions(goals.data ?? []);
  /*
    The goals this task serves, with their descriptions — flattened from the tree the picker
    already loaded rather than fetched again. Only the ones that actually say something: a goal
    with an empty description would print its own title twice.
  */
  const servedGoals = flattenTree(goals.data ?? [])
    .map((node) => node.goal)
    .filter((goal) => t.goalIds.includes(goal.id) && goal.descriptionMd.trim() !== '');
  // Whose group it is, not just its name — see `labelGroupCaption`.
  const groupNames = new Map(
    (labelGroups.data ?? []).map((g) => [g.id, labelGroupCaption(g, projects.data ?? [])]),
  );
  const labelOptions = (allLabels.data ?? []).map((l) => ({
    value: l.id,
    label: l.name,
    data: { color: l.color, groupName: groupNames.get(l.groupId) ?? '' },
  }));

  /*
    One statement of what each section holds, what it would be offered as, and who may.

    A table because the same eight keys are read three ways — whether the section renders, whether
    an offer for it belongs in *More*, and what that offer says — and three hand-kept lists of
    eight is the shape this screen has already been bitten by twice (`activeFilters` against the
    chip row, and `COMMANDS` against `keys.ts`). The role gates come with each entry because they
    used to live inside the child that drew the offer, and the offer has moved.

    `available` is the rule `FacetSection` used to carry: an offer to "add labels" on an account
    with no labels opens a picker with nothing in it. Subtasks has a shape of its own — a task that
    already has a parent cannot take children, which is the one-level nesting constraint, so there
    is nothing to offer it either.
  */
  const facets: {
    facet: Facet;
    label: string;
    /** The sentence beside the offer, kept on screen per `378daaa` — now inside *More*. */
    subtext?: string;
    testId?: string;
    empty: boolean;
    available: boolean;
    allowed: boolean;
    /**
     * The section itself states why it cannot be offered, so it renders even with no offer.
     *
     * Only `subtasks` does this, and it is not decoration: nesting stops at one level, so a task
     * that already has a parent gets *"Subtasks are one level deep, so this one cannot have its
     * own"* exactly where the control would have been. `Subtasks.tsx` says why — "the rule is
     * stated where the control was, because 'why can't I' is the question a missing control
     * raises". Without this flag `available: false` hid the answer along with the offer, which
     * `subtaskNesting.e2e.spec.ts` caught on the first pipeline.
     */
    saysWhyNot?: boolean;
  }[] = [
    {
      facet: 'labels',
      label: 'add labels',
      empty: t.labelIds.length === 0,
      available: labelOptions.length > 0,
      allowed: true,
    },
    {
      facet: 'color',
      label: 'add a colour',
      /* Nothing set *and* nothing to inherit from. Colour falls back to the task's goal, so a task
         with a goal still shows the control — the value it would inherit is worth seeing. */
      empty: t.color === null && t.goalIds.length === 0,
      available: true,
      allowed: true,
    },
    {
      facet: 'goals',
      label: 'add a goal',
      empty: t.goalIds.length === 0,
      available: goalPickerOptions.length > 0,
      allowed: true,
    },
    {
      facet: 'checklist',
      label: '+ a step',
      subtext: 'a line to tick off, with no date of its own',
      empty: t.checklist.total === 0,
      available: true,
      allowed: can('write'),
    },
    {
      facet: 'attachments',
      label: '+ a file',
      testId: 'add-attachment',
      empty: (attachments.data?.length ?? 0) === 0,
      available: true,
      allowed: can('assign'),
    },
    {
      facet: 'subtasks',
      label: '+ a subtask',
      subtext: 'a piece of work in its own right — it can have a day',
      empty: t.subtasks.total === 0,
      available: t.parentTaskId === null,
      allowed: can('write'),
      saysWhyNot: true,
    },
    {
      facet: 'links',
      label: '+ a link',
      testId: 'add-link',
      empty: (taskLinks.data?.length ?? 0) === 0,
      available: true,
      allowed: can('assign'),
    },
    {
      facet: 'notes',
      label: '+ a note',
      testId: 'add-note',
      empty: (notes.data?.length ?? 0) === 0,
      available: true,
      allowed: true,
    },
  ];
  const facetBy = new Map(facets.map((entry) => [entry.facet, entry] as const));
  /**
   * Whether the section itself is drawn, in its own place and outside the disclosure.
   *
   * An *or*: the task holds one, or the reader asked. Nothing with content is ever behind a shut
   * *More*, which is the rule — and it falls out of this rather than needing the disclosure's
   * `open` to carry it.
   */
  const shows = (facet: Facet) => {
    const entry = facetBy.get(facet)!;
    if ((!entry.empty && entry.available) || asked.has(facet)) return true;
    /* Unavailable *and* it has something to say about that — the section draws the rule in place
       of the control. Distinct from the labels and goals cases, where unavailable means the account
       has nothing to put there and silence is the right answer. */
    return !entry.available && entry.saysWhyNot === true;
  };
  /** What *More* offers: the empty ones the account can actually take. */
  const offers = facets.filter(
    (entry) => entry.empty && entry.available && !asked.has(entry.facet),
  );

  return (
    <aside {...asideProps}>
      <header className="detail-head">
        <input
          className="title-input"
          defaultValue={t.title}
          /* An input clips rather than ellipsises when focused, and cannot reveal the rest. */
          title={t.title}
          onBlur={(e) => {
            if (e.target.value.trim() && e.target.value !== t.title)
              save.mutate({ title: e.target.value.trim() });
          }}
        />
        {/*
          `icon-button`, not `link`.

          A bare `×` in a text button is as wide as the glyph — 13px, well under the 24px floor,
          on the control that closes the panel and therefore gets pressed more than anything else
          in it. On a phone that is a miss-tap waiting to happen.
        */}
        {onExpand && (
          <button
            className="icon-button"
            onClick={onExpand}
            aria-label="Open full view"
            title="Open full view"
          >
            ⤢
          </button>
        )}
        <button className="icon-button detail-close" onClick={onClose} aria-label="Close">
          ×
        </button>
      </header>

      {/*
        Finishing, at the top — on its own line rather than in the title's.

        Phase 26 moved this up from 942px below the fold, past eleven sections and beside a red
        Delete, which was right. Putting it *in* the header was not: the panel is a fixed 384px
        column, and Complete at 87px plus two icon buttons left the title 185px of room for 984px
        of text, so the panel clipped the name of the thing it was showing, mid-word, with no
        ellipsis and no tooltip. Two personas measured it independently.

        A row of its own costs one line and gives the title the width back. It is still the first
        control in the panel, which is the whole point of the move.
      */}
      <div className="detail-actions">
        <button
          type="button"
          className="detail-complete"
          onClick={() => finish.mutate()}
          disabled={t.status === 'done' || !can('assign')}
          {...(whyNotProgress ? { title: whyNotProgress } : {})}
        >
          {t.status === 'done' ? STATUS_VERB.done.past : STATUS_VERB.done.verb}
        </button>
      </div>
      {whyNot && (
        <p className="subtext" data-testid="role-note">
          {whyNot}
        </p>
      )}
      {/*
        One note for the panel's writes rather than one per control: at most one of them has just
        been pressed, and three stacked `.error` paragraphs in this column would push the task off
        the screen.
      */}
      <FailureNote error={finish.error ?? save.error ?? setLabels.error} />

      {/*
        A way up, under the title.

        The panel used to open on a subtask and give no sign it was one — `parentTaskId` was on the
        payload and nothing read it — so the answer to "why is this here" was a search. On this
        surface the name is always drawn: there is room, and this is where someone asks.
      */}
      {t.parent && (
        <p className="detail-parent">
          <ParentLink parent={t.parent} onOpen={onOpenTask} />
        </p>
      )}

      {/*
        That it repeats, next to the parent line because they answer the same question.

        "Why is this here" was answerable for a subtask and not for an occurrence: the panel had
        twenty controls and not one of them was about recurrence, while `get_task` had been handing
        an agent the rule since it was added. A person's only clue was a second task appearing after
        they completed this one.

        The sentence, not the rule, and read-only — a task's rule belongs to the template it came
        from, which is also why there is nothing to press here.
      */}
      {t.recurrenceText && (
        <p className="detail-recurs">
          <span className="pill">{t.recurrenceText}</span>
        </p>
      )}

      {/* Who has it, on the screen where the question is actually asked. */}
      <AssigneeLine task={t} />

      {/*
        The other way a task can be off your plate: somebody outside the app has it.

        Directly under "Held by", because they answer the same question — who is expected to act
        next — at the same `assign` rung, and reading them together is how a panel says "assigned
        to you, and you are waiting on Acme" rather than leaving that in two places.

        Deliberately *not* beside `BlockedBanner` below, which prints "Blocked by <task title>"
        about an open blocker. The two sentences stacked would be one word doing two jobs an inch
        apart; separated, and with the party's name in this one, they read as the different facts
        they are.
      */}
      <WaitingBanner task={t} />
      <WaitingOnField task={t} />

      <SubtaskOffer task={t} />

      <BlockedBanner task={t} {...(onOpenTask ? { onOpenTask } : {})} />

      <div className="fields">
        {/*
          The last two native selects carrying a closed vocabulary.

          Phases 55-60 replaced the app's `<select>`s with a picker that can draw a swatch, a badge
          or an icon beside each option, and the owner asked for "colors and icons to statuses,
          priorities" in the same breath. These two were missed: the status badge appeared on every
          task row and every board card, while the control you actually *set* the status with
          showed `in_progress` with the underscore swapped for a space.
        */}
        <label>
          Status
          <Select
            options={statusOptions()}
            value={t.status}
            onChange={(next) => next && save.mutate({ status: next as never })}
            renderOption={renderStatusOption}
            ariaLabel="Status"
            allowEmpty={false}
          />
        </label>

        <label>
          Priority
          <Select
            options={priorityOptions()}
            value={String(t.priority)}
            onChange={(next) => next && save.mutate({ priority: Number(next) })}
            renderOption={renderPriorityOption}
            ariaLabel="Priority"
            allowEmpty={false}
          />
        </label>

        <label>
          Due
          <input
            type="date"
            defaultValue={t.dueAt ? t.dueAt.slice(0, 10) : ''}
            onChange={(e) =>
              save.mutate({ dueAt: e.target.value ? new Date(e.target.value).toISOString() : null })
            }
          />
        </label>
      </div>

      {/*
        Estimate, Points and Scheduled for, behind one disclosure.

        They were three of six equal `<label>` rows, so on "buy milk" the panel gave Points and
        Estimate the same billing as Due — a DOM count returned twenty controls inside the drawer,
        and a persona read the whole thing as "a decision I now have to make". Status, Priority and
        Due are what a task is triaged on; a size and a plan are what you fill in when you are
        planning one, which is a different sitting.

        Open when any of the three is set, so a sized task shows its size and "buy milk" does not.
        `<details>` rather than state, because that is exactly what the element is for and it comes
        with the keyboard and the announcement already right — and `open` is computed rather than
        held, so a task that gains an estimate while the panel is up does not need a second source
        of truth for whether the group is showing.

        Nothing is removed: the clutter rule here is less noise, not fewer controls, and all three
        are one press away.

        **Since Phase 102 the offers are in here too, against this comment's earlier reading.** It
        used to say colour stayed out because its `FacetSection` already collapsed to a single "add
        a colour" link, and that nesting one inside the other would put two presses between a
        reader and a swatch. The one-line collapse *was* the right outcome for a section's
        furniture, and the two presses are a real cost. What the comment did not weigh is that the
        outcome it was defending happened seven times over: seven one-line offers stacked under a
        two-word task is the object four persona rounds measured — `01a0337a-102d` (1.0.0),
        `01a03813-cef2` (1.7.0, "a two-word task above nine offers … 21 visible controls"),
        `01a059c7-6a50` (1.10.0), `01a08097-14ec` (1.39.0, 20 controls) — and every one of those
        rounds' own suggestions put the offers behind a single press. Nobody in four rounds reported
        that an offer was hard to reach.

        So the rule is: an offer for something the task does not hold lives in here; a section that
        holds something renders in its own place, outside, and is never behind a shut *More*. The
        description is content rather than a facet and stays in the bare panel. `open` still reads
        only the three fields — an offer is empty by definition and has nothing to be open for.

        What would reopen it: a round reporting an offer was *not found* from the panel, as opposed
        to merely further away.
      */}
      <details
        className="more-fields"
        data-testid="more-fields"
        open={t.estimateMinutes !== null || t.complexityPoints !== null || t.scheduledDate !== null}
      >
        <summary>More</summary>
        <div className="fields">
          <label>
            Estimate (min)
            <input
              type="number"
              min={1}
              defaultValue={t.estimateMinutes ?? ''}
              onBlur={(e) =>
                save.mutate({ estimateMinutes: e.target.value ? Number(e.target.value) : null })
              }
            />
          </label>

          <label>
            Points
            <Select
              options={POINTS.map((p) => ({ value: String(p), label: String(p) }))}
              value={t.complexityPoints === null ? null : String(t.complexityPoints)}
              onChange={(next) => save.mutate({ complexityPoints: next ? Number(next) : null })}
              ariaLabel="Points"
              emptyLabel="—"
              placeholder="—"
            />
          </label>

          <label>
            Scheduled for
            <input
              type="date"
              defaultValue={t.scheduledDate ?? ''}
              onChange={(e) => save.mutate({ scheduledDate: e.target.value || null })}
            />
          </label>
        </div>
        {/*
          And what this task could hold but does not.

          One list, so the disclosure reads as "what you could add to this task" rather than as a
          drawer of leftover fields. Each entry is the offer its own section used to draw on the
          panel; pressing one records the ask and the section appears in its own place, outside
          here, which is why nothing with content is ever behind a shut *More*.

          Greyed rather than dropped when the role forbids it, which is the call `Complete` and the
          old inline offers both made: a control that has quietly gone is a thing the reader cannot
          ask about.
        */}
        {offers.length > 0 && (
          <div data-testid="more-offers">
            {offers.map((entry) => (
              /*
                One `.panel-empty` per offer, which is what the stylesheet is built for.

                That class is `display: inline-flex` with a right margin, so sibling paragraphs are
                inline-level boxes that flow onto shared lines *and wrap* — which is how six offers
                fit a 384px panel. Putting them all inside one `.panel-empty` instead made them flex
                items on a single line with no `flex-wrap`, and at 375 three of them ran off the
                right edge: `+ a note` reached x=554 in a 375px viewport. `reachOnAPhone`'s sweep
                caught it, by measuring the boxes a shut `<details>` still has.

                It also keeps each button a *direct* child, so `.panel-empty > button { flex: none }`
                applies — the rule that stops a label giving way to its own descriptor, which
                `+ a subtask` needed when it measured two lines for a ten-character label.
              */
              <p className="panel-empty" key={entry.facet}>
                <button
                  type="button"
                  className="link"
                  onClick={() => ask(entry.facet)}
                  disabled={!entry.allowed}
                  {...(entry.testId ? { 'data-testid': entry.testId } : {})}
                >
                  {entry.label}
                </button>{' '}
                {entry.subtext && <span className="subtext">{entry.subtext}</span>}
              </p>
            ))}
          </div>
        )}
      </details>

      {/*
        The Project panel is gone.

        Projects were retired in favour of labels — every one became a label carrying its colour in
        migration 0019, and the Labels picker above replaces this. The `project_id` column and the
        rows behind it are untouched, so this is a UI removal rather than a deletion.
      */}

      {/*
        A section appears when there is something in it, or when you ask for it.

        "buy milk" opened about 1,250px of empty scaffolding: six blank fields, a nine-button
        markdown toolbar and a Preview tab above an empty textarea, then Attachments with a raw
        file picker, "No links." and "Nothing yet." Phase 26 collapsed steps and subtasks and
        stopped there. The rest of the panel is the same argument: the furniture of a field is not
        worth showing to somebody who has not asked to fill it in.
      */}
      {description === '' && !writing ? (
        <p className="panel-empty">
          <button className="link" onClick={() => setWriting(true)} data-testid="add-description">
            + a description
          </button>
        </p>
      ) : (
        <div className="panel">
          <h3>Description</h3>
          <MarkdownEditor
            label="Description"
            value={description}
            placeholder="Markdown — use the toolbar for a table"
            onChange={setDescription}
            // Saved on blur, as before: a save on every keystroke would put a request
            // behind every letter.
            onBlur={() => {
              if (description !== t.descriptionMd) save.mutate({ descriptionMd: description });
            }}
          />
        </div>
      )}

      {shows('labels') && (
        <FacetSection title="Labels">
          {/*
          One control for every group, and picking a second answer from a group that allows one
          replaces the first — enforced by `applyLabels`, not here.

          That sentence used to be a `<p className="muted">` above this picker, shown to every user
          on every task. "The rule lives in the service, not here" explains an implementation
          decision to another engineer; it tells the reader nothing to do, and it took permanent
          vertical space above the control a member uses to file a task under the project's
          taxonomy. The picker teaches the rule by doing it.
        */}
          <MultiSelect
            options={labelOptions}
            value={t.labelIds}
            /* `t.labelIds` is the set this picker was rendered with, which is what the click decided
             against — see the mutation for why it is captured here and not read inside it. */
            onChange={(next) => setLabels.mutate({ next, previous: t.labelIds })}
            placeholder="No labels"
            ariaLabel="Labels"
            dialogTitle="Labels on this task"
            renderOption={(option) => (
              <>
                <span
                  className={`swatch ${option.data?.color ? `goal-c-${option.data.color}` : ''}`}
                />
                <span className="t">{option.label}</span>
                <span className="sub">{option.data?.groupName}</span>
              </>
            )}
            renderChip={(option) => (
              <>
                <span
                  className={`swatch ${option.data?.color ? `goal-c-${option.data.color}` : ''}`}
                />
                {option.label}
              </>
            )}
          />
        </FacetSection>
      )}

      {shows('color') && (
        <FacetSection title="Colour">
          {/*
          The order the resolver actually uses, which this line got wrong in two ways: it never
          mentioned labels, which win, and it named the project as first. A help text that
          disagrees with the code teaches the wrong model to the one person reading it.
        */}
          <p className="muted">
            Leave it on Auto and this task takes its parent’s colour, then its first coloured
            label’s, then its project’s, then its goal’s.
          </p>
          <ColorPicker
            name={`task-color-${t.id}`}
            label="Colour"
            value={t.color}
            onChange={(color) => save.mutate({ color })}
          />
        </FacetSection>
      )}

      {shows('goals') && (
        <FacetSection title="Goals">
          <p className="muted">A task can serve several goals at once.</p>
          {/*
          One control instead of a column of checkboxes.

          A passing spec used to say driving this list "costs more than it proves" and attached
          the goal through the service instead — which is a fair description of a controlled
          checkbox list that re-renders on every server answer, and a sign the control was wrong.
        */}
          <MultiSelect
            options={goalPickerOptions}
            value={t.goalIds}
            onChange={(next) => save.mutate({ goalIds: next })}
            placeholder="No goals"
            ariaLabel="Serves goals"
            dialogTitle="Goals this task serves"
            renderOption={renderGoalOption}
            renderChip={(option) => (
              <>
                <span className={`swatch ${option.data?.colorClass ?? ''}`} />
                {option.label}
              </>
            )}
          />
          {goalPickerOptions.length === 0 && <p className="muted">No goals yet.</p>}

          {/*
          Why this goal exists, next to the task doing it.

          A goal's description is the one place anybody writes down what it is *for*, and it was
          readable only on the Goals screen — so the moment you were actually deciding whether to
          do this task, the reason it mattered was two navigations away. Shown read-only and
          truncated: this is context for a decision, not a second place to edit the goal.
        */}
          {servedGoals.map((goal) => (
            <p key={goal.id} className="task-goal-why">
              <strong>{goal.title}</strong>
              <span>{firstLine(goal.descriptionMd)}</span>
            </p>
          ))}
        </FacetSection>
      )}

      {shows('checklist') && (
        <Checklist taskId={taskId} projectId={t.projectId} startOpen={asked.has('checklist')} />
      )}

      <Reminders taskId={taskId} />

      {shows('attachments') && (
        <Attachments taskId={taskId} projectId={t.projectId} startOpen={asked.has('attachments')} />
      )}

      {/*
        Always rendered now, not only when there are subtasks — the input is how the first one gets
        made, so hiding the section until one exists hid the only way to create it.
      */}
      {shows('subtasks') && (
        <Subtasks
          taskId={taskId}
          projectId={t.projectId}
          parent={t}
          onOpenTask={onOpenTask}
          startOpen={asked.has('subtasks')}
        />
      )}

      {shows('links') && (
        <Links
          taskId={taskId}
          projectId={t.projectId}
          startOpen={asked.has('links')}
          {...(onOpenTask ? { onOpenTask } : {})}
        />
      )}

      {shows('notes') && <Thread taskId={taskId} startOpen={asked.has('notes')} />}

      {/* Complete has moved to the header — see there. What is left is the pair that belongs at
          the bottom: one that makes something, and one that destroys something. */}
      <footer className="detail-foot">
        {/*
          No confirmation: cloning makes something new and destroys nothing, and the copy is one
          Delete away if it was a mistake.
        */}
        <button onClick={() => clone.mutate()} disabled={clone.isPending}>
          {clone.isPending ? 'Cloning…' : 'Clone'}
        </button>
        <button
          className="danger"
          onClick={() => {
            if (confirm(deleteTaskPrompt(t))) remove.mutate();
          }}
        >
          Delete
        </button>
      </footer>
    </aside>
  );
}
